<template>
  <b-card>
    <!-- form -->
    <b-form>
      <b-row>
        <!-- old password -->
        <b-col md="6">
          <b-form-group
            :label="$t('Old Password')"
            label-for="account-old-password"
          >
            <b-input-group class="input-group-merge">
              <b-form-input
                id="account-old-password"
                v-model="passwordValueOld"
                name="old-password"
                :type="passwordFieldTypeOld"
                :placeholder="$t('Enter your old password')"
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="passwordToggleIconOld"
                  class="cursor-pointer"
                  @click="togglePasswordOld"
                />
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <!--/ old password -->
      </b-row>
      <b-row>
        <!-- new password -->
        <b-col md="6">
          <b-form-group
            label-for="account-new-password"
            :label="$t('New Password')"
          >
            <b-input-group class="input-group-merge">
              <b-form-input
                id="account-new-password"
                ref="mainPwEntry"
                v-model="newPasswordValue"
                :type="passwordFieldTypeNew"
                name="new-password"
                :placeholder="$t('Enter your new password')"
                lazy-formatter
                :formatter="passwordFormatter"
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="passwordToggleIconNew"
                  class="cursor-pointer"
                  @click="togglePasswordNew"
                />
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <!--/ new password -->
        <!-- retype password -->
        <b-col md="6">
          <b-form-group
            label-for="account-retype-new-password"
            :label="$t('Retype New Password')"
          >
            <b-input-group class="input-group-merge">
              <b-form-input
                id="account-retype-new-password"
                v-model="RetypePassword"
                :type="passwordFieldTypeRetype"
                name="retype-password"
                :placeholder="$t('Enter your new password again')"
                @blur="comparePwds"
              />
              <b-input-group-append is-text>
                <feather-icon
                  :icon="passwordToggleIconRetype"
                  class="cursor-pointer"
                  @click="togglePasswordRetype"
                />
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
        <!--/ retype password -->
        <!-- buttons -->
        <b-col cols="12">
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mt-1 mr-1"
            :disabled="awaitingCode"
            @click="changePassword()"
          >
            {{ $t("Save Changes") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            type="reset"
            variant="outline-secondary"
            class="mt-1"
          >
            {{ $t("Reset") }}
          </b-button>
        </b-col>
        <!--/ buttons -->
        <!-- OTP step -->
        <b-col
          v-if="awaitingCode"
          cols="12"
          class="mt-2"
        >
          <b-form-group
            :label="$t('Verification Code')"
            label-for="password-otp-code"
          >
            <b-form-input
              id="password-otp-code"
              v-model="code"
              maxlength="6"
              inputmode="numeric"
              autocomplete="one-time-code"
              :formatter="codeFormatter"
            />
          </b-form-group>
          <small
            v-if="codeError"
            class="text-danger d-block"
          >{{ codeError }}</small>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mt-1 mr-1"
            :disabled="!codeValid || restartRequired"
            @click="verifyPasswordOtp()"
          >
            {{ $t("Verify") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mt-1"
            @click="cancelOtp()"
          >
            {{ $t("Cancel") }}
          </b-button>
        </b-col>
        <!--/ OTP step -->
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BCard,
  BInputGroup,
  BInputGroupAppend,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import axios from '@axios'
import useJwt from '@/auth/jwt/useJwt'
import { SOURCE_PASSWORD, setPhoneSetupHandoff } from '@/auth/phoneSetupHandoff'
import { OTP_ERROR_KEYS, OTP_ERROR_KEYS_BY_CODE } from '@/libs/otpI18nKeys'

/* eslint-disable import/extensions, import/no-unresolved, import/no-absolute-path */
const { axiosRoutes: { auth: authRoutes } } = require('/G_CONFIG.js')
/* eslint-enable import/extensions, import/no-unresolved, import/no-absolute-path */

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BCard,
    BInputGroup,
    BInputGroupAppend,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      passwordValueOld: '',
      newPasswordValue: '',
      RetypePassword: '',
      passwordFieldTypeOld: 'password',
      passwordFieldTypeNew: 'password',
      passwordFieldTypeRetype: 'password',
      awaitingCode: false,
      challengeId: '',
      code: '',
      codeError: '',
      restartRequired: false,
    }
  },
  computed: {
    passwordToggleIconOld() {
      return this.passwordFieldTypeOld === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    passwordToggleIconNew() {
      return this.passwordFieldTypeNew === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    passwordToggleIconRetype() {
      return this.passwordFieldTypeRetype === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    codeValid() {
      return /^\d{6}$/.test(this.code)
    },
  },
  methods: {
    togglePasswordOld() {
      this.passwordFieldTypeOld = this.passwordFieldTypeOld === 'password' ? 'text' : 'password'
    },
    togglePasswordNew() {
      this.passwordFieldTypeNew = this.passwordFieldTypeNew === 'password' ? 'text' : 'password'
    },
    togglePasswordRetype() {
      this.passwordFieldTypeRetype = this.passwordFieldTypeRetype === 'password' ? 'text' : 'password'
    },
    changePassword() {
      if (!this.newPasswordValue || this.newPasswordValue.length < 8) {
        this.$swal({
          title: this.$t('Error'),
          text: this.$t('Password not long enough (min 8 characters))'),
          icon: 'error',
          confirmButtonText: this.$t('Ok'),
        })
        return
      }
      if (!this.comparePwds()) {
        return
      }
      document.getElementById('loading-bg').style.display = 'block'
      axios
        .post(authRoutes.passwordChangeRequest, {
          oldPassword: this.passwordValueOld,
          newPassword: this.newPasswordValue,
        })
        .then(response => {
          if (response.data && response.data.phoneSetupRequired) {
            setPhoneSetupHandoff({
              token: response.data.phoneSetupToken,
              tokenExpiresAt: Date.now() + (response.data.phoneSetupExpiresInSeconds || 600) * 1000,
              source: SOURCE_PASSWORD,
              credential: useJwt.currentCredentialVersion(),
              returnTo: this.$route.fullPath,
            })
            this.$router.push({ name: 'phone-setup' })
            return
          }
          this.challengeId = response.data.challengeId
          this.code = response.data.devCode || ''
          this.awaitingCode = true
          this.codeError = ''
        })
        .finally(() => {
          document.getElementById('loading-bg').style.display = 'none'
        })
    },
    codeFormatter(value) {
      return String(value == null ? '' : value).replace(/\D/g, '').slice(0, 6)
    },
    async verifyPasswordOtp() {
      if (!this.codeValid) return
      document.getElementById('loading-bg').style.display = 'block'
      try {
        await useJwt.runFactorVerification(authRoutes.passwordChangeVerify, {
          challengeId: this.challengeId,
          code: this.code,
        })
        this.$swal({
          title: this.$t('Success'),
          text: this.$t('Password changed successfully'),
          icon: 'success',
          confirmButtonText: this.$t('Ok'),
        })
        this.cancelOtp()
      } catch (error) {
        if (this.isFactorRestartRequired(error)) {
          this.restartRequired = true
          this.codeError = this.$t('Verification service is restarting. Please try again in a moment.')
        } else {
          const data = (error.response && error.response.data) || {}
          const mapped = OTP_ERROR_KEYS_BY_CODE[Number(data.errorCode)]
          this.codeError = mapped ? this.$t(mapped) : (data.message || this.$t(OTP_ERROR_KEYS.invalidCode))
        }
      } finally {
        document.getElementById('loading-bg').style.display = 'none'
      }
    },
    // CODE-R1-013: see PhoneOtpModal.vue's identical helper — a factor
    // rotation requiring a restart surfaces either as the server's
    // 503/503003, or as a synchronous CredentialStateError from useJwt's
    // armFactorPending guard on any re-attempt after that state is recorded.
    isFactorRestartRequired(error) {
      const response = error && error.response
      if (response && response.status === 503 && Number(response.data && response.data.errorCode) === 503003) return true
      return Boolean(error && error.name === 'CredentialStateError' && /restart/i.test(error.message || ''))
    },
    cancelOtp() {
      this.awaitingCode = false
      this.challengeId = ''
      this.code = ''
      this.codeError = ''
      this.newPasswordValue = ''
      this.RetypePassword = ''
      this.passwordValueOld = ''
    },
    passwordFormatter(value) {
      if (value.length > 0 && value.length < 8) {
        this.$swal({
          title: this.$t('Error'),
          text: this.$t('Password not long enough (min 8 characters))'),
          icon: 'error',
          confirmButtonText: this.$t('Ok'),
        })
        return ''
      }
      return value
    },
    comparePwds() {
      if (this.newPasswordValue !== this.RetypePassword) {
        this.$swal({
          title: this.$t('Error'),
          text: this.$t('Passwords do not match'),
          icon: 'error',
          confirmButtonText: this.$t('Ok'),
        })
        this.RetypePassword = ''
        return false
      }
      return true
    },
  },
}
</script>
